import React, { useState } from 'react';
import { FileText, Download, Shield, MoreVertical } from 'lucide-react';
import { Document } from '../../types/documents';
import { format, parseISO, isValid } from 'date-fns';
import DocumentPermissionsModal from './DocumentPermissionsModal';

interface DocumentListProps {
  documents: Document[];
  onUpload: () => void;
}

export default function DocumentList({ documents, onUpload }: DocumentListProps) {
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) {
        return 'Invalid date';
      }
      return format(date, 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold text-text-primary mb-4">Documents</h2>

      <div className="space-y-2">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center gap-4 p-4 rounded-lg bg-background hover:bg-primary-50 
                     transition-colors group"
          >
            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary-600" />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-text-primary truncate">{doc.name}</h3>
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <span>{formatFileSize(doc.size)}</span>
                <span>•</span>
                <span>{formatDate(doc.createdAt)}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDocument(doc);
                  setShowPermissionsModal(true);
                }}
                className="p-2 text-text-secondary hover:text-primary-500 transition-colors"
                title="Manage Permissions"
              >
                <Shield className="w-5 h-5" />
              </button>
              
              <a
              href={doc.url}
              download={doc.name}
              className="p-2 text-text-secondary hover:text-primary-500 transition-colors 
                       opacity-0 group-hover:opacity-100"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download className="w-5 h-5" />
            </a>
            </div>
          </div>
        ))}

        {documents.length === 0 && (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-text-secondary mx-auto mb-3" />
            <p className="text-text-secondary">No documents in this folder</p>
            <button
              onClick={onUpload}
              className="mt-2 text-primary-500 hover:text-primary-600 font-medium"
            >
              Upload documents
            </button>
          </div>
        )}
      </div>
      
      {showPermissionsModal && selectedDocument && (
        <DocumentPermissionsModal
          document={selectedDocument}
          onClose={() => {
            setShowPermissionsModal(false);
            setSelectedDocument(null);
          }}
          onUpdate={(updatedDoc) => {
            // Update document in list if needed
            console.log('Document updated:', updatedDoc);
          }}
        />
      )}
    </div>
  );
}